import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import AppDrawer from './AppDrawer';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';

// Shell reutilizable para todas las pantallas autenticadas (ADMIN y
// ENCARGADO): header + drawer deslizable en móvil/tablet, sidebar
// persistente desde `lg`, mismo contenido de navegación en ambos casos
// (AppDrawer), un solo lugar donde vive la lógica responsive.
function AppShell({ children, title, backTo }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { isAdmin, admin, encargado, productor } = useAuth();
  const online = useNetworkStatus();

  // Cerrar el drawer al cambiar de ruta (incluye seleccionar una opción).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // ESC cierra el drawer.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') setDrawerOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // Evita que el contenido detrás se desplace mientras el drawer móvil
  // está abierto; se restaura solo (cleanup) al cerrarlo.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  const nombre = isAdmin ? admin?.nombre : encargado?.nombre;
  const contexto = isAdmin ? 'Administrador' : productor?.nombre;
  const headerTitle = title || 'Family Growers';

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Sidebar persistente en desktop */}
      <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:flex-shrink-0 lg:flex-col lg:border-r lg:border-gray-100 lg:bg-white">
        <AppDrawer />
      </aside>

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-gray-100 bg-white px-3 sm:h-16 sm:px-4">
          {backTo ? (
            <Link
              to={backTo}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              aria-label="Volver"
            >
              <Icon name="back" />
            </Link>
          ) : (
            <button
              type="button"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 lg:hidden"
              aria-label="Abrir menú"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="menu" />
            </button>
          )}

          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <p className="truncate text-base font-semibold text-gray-900 sm:text-lg">{headerTitle}</p>

            <div className="hidden items-center gap-2 lg:flex">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{nombre}</p>
                <p className="text-xs text-gray-500">{contexto}</p>
              </div>
              <Avatar nombre={nombre} />
            </div>
          </div>

          {!backTo && (
            <div className="shrink-0 lg:hidden">
              <Avatar nombre={nombre} />
            </div>
          )}
        </header>

        {/* Offline: ADMIN requiere Internet, así que este aviso solo aplica
            a ENCARGADO — el módulo con soporte offline (Aplicaciones).
            Deliberadamente breve/discreto: el detalle (cuántas aplicaciones
            pendientes y el botón Sincronizar) vive en Aplicaciones, donde
            aporta valor real; aquí solo se avisa que se puede seguir
            trabajando. */}
        {!isAdmin && !online && (
          <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 text-sm text-amber-800 sm:px-6 lg:px-8">
            <Icon name="wifiOff" className="h-4 w-4 shrink-0" />
            <p>
              <span className="font-medium">Sin conexión.</span> Puedes seguir trabajando.
            </p>
          </div>
        )}

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 xl:px-8">
          <div className="w-full pb-[env(safe-area-inset-bottom)]">{children}</div>
        </main>
      </div>

      {/* Drawer móvil/tablet (oculto desde lg, donde ya hay sidebar) */}
      <div className={`fixed inset-0 z-40 lg:hidden ${drawerOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />

        <div
          className={`absolute inset-y-0 left-0 flex w-[85%] max-w-[320px] flex-col bg-white shadow-xl transition-transform duration-300 ease-out ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!drawerOpen}
        >
          <div className="flex justify-end px-3 pt-3">
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              aria-label="Cerrar menú"
              onClick={() => setDrawerOpen(false)}
            >
              <Icon name="close" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AppDrawer onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppShell;
